import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, Animated } from 'react-native';
import { evaluateJobs } from '../services/openaiService';
import { useTheme } from '../contexts/ThemeContext';
import LandingStyleHeader from '../components/LandingStyleHeader';

export default function JobSelectorScreen({ navigation }) {
  const { colors, isDark, gradients } = useTheme();
  const [jobBatch, setJobBatch] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredButtonId, setHoveredButtonId] = useState(null);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const resultsAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    document.title = 'Flomodo - Job Selector';
    
    // Entrance animation
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();

    // Check for pre-filled content from dashboard
    const pendingContent = sessionStorage.getItem('pendingJobBatch');
    if (pendingContent) {
      setJobBatch(pendingContent);
      sessionStorage.removeItem('pendingJobBatch');
      // Auto-evaluate after a short delay
      setTimeout(() => {
        handleEvaluate(pendingContent);
      }, 500);
    }
  }, []);

  const handleEvaluate = async (content = null) => {
    const jobContent = content || jobBatch;
    if (!jobContent.trim()) {
      setError('Please paste job posts to evaluate');
      return;
    }

    setLoading(true);
    setError(null);
    setEvaluation(null);
    
    try {
      const result = await evaluateJobs(jobContent);
      setEvaluation(result);
      
      // Animate results in
      Animated.spring(resultsAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }).start();
    } catch (err) {
      console.error('Job evaluation error:', err);
      setError(err.message || 'Failed to evaluate jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setJobBatch('');
    setEvaluation(null);
    setError(null);
    resultsAnim.setValue(0);
  };

  const getOddsColor = (odds) => {
    if (odds >= 65) return '#4CAF50';
    if (odds >= 40) return '#FFA726';
    return '#EF5350';
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.primary, backgroundImage: isDark ? gradients.background : 'none' }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="jobselector" />
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        onScroll={(e) => {
          const scrollY = e.nativeEvent.contentOffset.y;
          window.lastKnownScrollY = scrollY;
          window.dispatchEvent(new Event('scroll'));
        }}
        scrollEventThrottle={16}
      >
        <Animated.View
          style={[
            styles.content,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }]
            }
          ]}
        >
          {/* Hero Header */}
          <View style={styles.heroHeader}>
            <View style={styles.heroIcon}>
              <Text style={styles.heroIconText}>🎯</Text>
            </View>
            <Text style={[styles.title, { color: colors.text }]}>Job Selector</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Paste job posts from Upwork, Contra, or any freelance platform. 
              We'll rank them by quality and tell you which ones are worth applying to.
            </Text>
          </View>

          {/* Input Card */}
          <View style={[styles.inputCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.inputHeader}>
              <Text style={[styles.inputTitle, { color: colors.text }]}>📋 Job Posts</Text>
              <Text style={[styles.inputSubtitle, { color: colors.textSecondary }]}>
                Paste 5-20 jobs for best results
              </Text>
            </View>
            
            <TextInput
              style={[styles.textArea, { 
                color: colors.text, 
                backgroundColor: colors.primary,
                borderColor: colors.border
              }]}
              multiline
              placeholder="Paste job posts here...

Example format:
────────────────────────────────
Job Title: Senior React Developer
Budget: $15,000 - $20,000
Description: Looking for experienced developer...
────────────────────────────────

(Paste multiple jobs separated by lines)"
              placeholderTextColor={colors.textSecondary}
              value={jobBatch}
              onChangeText={setJobBatch}
              editable={!loading}
            />
            
            {error && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorIcon}>⚠️</Text>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}

            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[
                  styles.button, 
                  styles.primaryButton,
                  loading && styles.buttonDisabled,
                  hoveredButtonId === 'evaluate' && styles.buttonHovered
                ]}
                onPress={() => handleEvaluate()}
                disabled={loading}
                onMouseEnter={() => setHoveredButtonId('evaluate')}
                onMouseLeave={() => setHoveredButtonId(null)}
              >
                {loading ? (
                  <View style={styles.buttonContent}>
                    <ActivityIndicator color="#fff" size="small" />
                    <Text style={styles.buttonText}>Evaluating...</Text>
                  </View>
                ) : (
                  <View style={styles.buttonContent}>
                    <Text style={styles.buttonIcon}>🎯</Text>
                    <Text style={styles.buttonText}>Evaluate Jobs</Text>
                  </View>
                )}
              </TouchableOpacity>

              {(jobBatch || evaluation) && (
                <TouchableOpacity
                  style={[
                    styles.button, 
                    styles.secondaryButton,
                    hoveredButtonId === 'clear' && styles.secondaryButtonHovered
                  ]}
                  onPress={handleClear}
                  disabled={loading}
                  onMouseEnter={() => setHoveredButtonId('clear')}
                  onMouseLeave={() => setHoveredButtonId(null)}
                >
                  <Text style={[styles.buttonText, styles.secondaryButtonText]}>Clear</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Results Section */}
          {evaluation && (
            <Animated.View
              style={[
                styles.resultsContainer,
                {
                  opacity: resultsAnim,
                  transform: [{
                    translateY: resultsAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [30, 0],
                    }),
                  }],
                }
              ]}
            >
              {/* Summary Stats */}
              <View style={[styles.statsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.statsTitle, { color: colors.text }]}>📊 Evaluation Summary</Text>
                <View style={styles.statsGrid}>
                  <View style={styles.statItem}>
                    <View style={[styles.statBadge, { backgroundColor: 'rgba(76, 175, 80, 0.2)' }]}>
                      <Text style={styles.statNumber}>{evaluation.applyTo?.length || 0}</Text>
                    </View>
                    <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Apply To</Text>
                  </View>
                  <View style={styles.statItem}>
                    <View style={[styles.statBadge, { backgroundColor: 'rgba(239, 83, 80, 0.2)' }]}>
                      <Text style={styles.statNumber}>{evaluation.skip?.length || 0}</Text>
                    </View>
                    <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Skip</Text>
                  </View>
                  <View style={styles.statItem}>
                    <View style={[styles.statBadge, { backgroundColor: 'rgba(162, 89, 255, 0.2)' }]}>
                      <Text style={styles.statNumber}>
                        {Math.round(((evaluation.applyTo?.length || 0) / ((evaluation.applyTo?.length || 0) + (evaluation.skip?.length || 0))) * 100) || 0}%
                      </Text>
                    </View>
                    <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Match Rate</Text>
                  </View>
                </View>
              </View>

              {/* Jobs to Apply To */}
              {evaluation.applyTo && evaluation.applyTo.length > 0 && (
                <View style={[styles.resultsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[styles.sectionTitle, { color: colors.text }]}>✅ Jobs to Apply To</Text>
                  <Text style={[styles.sectionSubtitle, { color: colors.textSecondary }]}>
                    Ranked from best to worst opportunity
                  </Text>
                  
                  <View style={styles.jobsList}>
                    {evaluation.applyTo.map((job, index) => (
                      <View 
                        key={index} 
                        style={[
                          styles.jobCard, 
                          { 
                            backgroundColor: colors.primary,
                            borderColor: colors.border,
                            borderLeftColor: getOddsColor(job.acceptanceOdds),
                            borderLeftWidth: 4,
                          }
                        ]}
                      >
                        <View style={styles.jobHeader}>
                          <View style={styles.rankBadge}>
                            <Text style={styles.rankText}>#{job.rank}</Text>
                          </View>
                          <Text style={[styles.jobTitle, { color: colors.text }]}>{job.title}</Text>
                        </View>
                        
                        <View style={styles.jobMetrics}>
                          <View style={styles.metric}>
                            <Text style={styles.metricLabel}>Decision</Text>
                            <View style={styles.decisionBadge}>
                              <Text style={styles.decisionText}>{job.decision}</Text>
                            </View>
                          </View>
                          
                          <View style={styles.metric}>
                            <Text style={styles.metricLabel}>Target Price</Text>
                            <Text style={[styles.metricValue, { color: colors.text }]}>{job.targetPrice}</Text>
                          </View>
                          
                          <View style={styles.metric}>
                            <Text style={styles.metricLabel}>Acceptance Odds</Text>
                            <View style={[styles.oddsBadge, { backgroundColor: getOddsColor(job.acceptanceOdds) + '20' }]}>
                              <Text style={[styles.oddsText, { color: getOddsColor(job.acceptanceOdds) }]}>
                                {job.acceptanceOdds}%
                              </Text>
                            </View>
                          </View>

                          {job.timeline && (
                            <View style={styles.metric}>
                              <Text style={styles.metricLabel}>Timeline</Text>
                              <View style={styles.timelineBadge}>
                                <Text style={styles.timelineIcon}>⏱️</Text>
                                <Text style={[styles.metricValue, { color: colors.text }]}>{job.timeline}</Text>
                              </View>
                            </View>
                          )}

                          {job.challenges && (
                            <View style={styles.metric}>
                              <Text style={styles.metricLabel}>Main Challenge</Text>
                              <View style={styles.challengeBadge}>
                                <Text style={styles.challengeIcon}>⚡</Text>
                                <Text style={[styles.metricValue, { color: colors.text, fontSize: 14 }]}>{job.challenges}</Text>
                              </View>
                            </View>
                          )}
                        </View>
                        
                        <View style={styles.framingSection}>
                          <Text style={styles.framingLabel}>💡 Strategic Framing</Text>
                          <Text style={[styles.framingText, { color: colors.text }]}>{job.framing}</Text>
                        </View>
                        
                        {job.risk && (
                          <View style={styles.riskSection}>
                            <Text style={styles.riskIcon}>⚠️</Text>
                            <Text style={[styles.riskText, { color: colors.textSecondary }]}>{job.risk}</Text>
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                </View>
              )}

              {/* Jobs to Skip */}
              {evaluation.skip && evaluation.skip.length > 0 && (
                <View style={[styles.resultsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                  <Text style={[styles.sectionTitle, { color: colors.text }]}>❌ Jobs to Skip</Text>
                  <Text style={[styles.sectionSubtitle, { color: colors.textSecondary }]}>
                    These don't match your premium positioning
                  </Text>
                  
                  <View style={styles.skipList}>
                    {evaluation.skip.map((job, index) => (
                      <View 
                        key={index} 
                        style={[
                          styles.skipCard, 
                          { 
                            backgroundColor: colors.primary,
                            borderColor: 'rgba(239, 83, 80, 0.3)',
                          }
                        ]}
                      >
                        <Text style={[styles.skipTitle, { color: colors.text }]}>{job.title}</Text>
                        <Text style={[styles.skipReason, { color: colors.textSecondary }]}>{job.reason}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </Animated.View>
          )}
        </Animated.View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 80,
  },
  content: {
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
    padding: 32,
  },
  heroHeader: {
    alignItems: 'center',
    marginBottom: 40,
    paddingTop: 40,
  },
  heroIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    backgroundColor: 'rgba(162, 89, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  heroIconText: {
    fontSize: 40,
  },
  title: {
    fontSize: 40,
    fontWeight: 'bold',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 18,
    lineHeight: 28,
    textAlign: 'center',
    maxWidth: 700,
  },
  inputCard: {
    borderRadius: 16,
    padding: 32,
    marginBottom: 32,
    borderWidth: 1,
  },
  inputHeader: {
    marginBottom: 20,
  },
  inputTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  inputSubtitle: {
    fontSize: 14,
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 20,
    fontSize: 14,
    minHeight: 300,
    maxHeight: 500,
    fontFamily: 'monospace',
    lineHeight: 20,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(239, 83, 80, 0.1)',
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
    borderWidth: 1,
    borderColor: 'rgba(239, 83, 80, 0.3)',
  },
  errorIcon: {
    fontSize: 16,
  },
  errorText: {
    color: '#EF5350',
    fontSize: 14,
    flex: 1,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  button: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    transition: 'all 0.2s ease',
  },
  primaryButton: {
    backgroundColor: '#A259FF',
  },
  buttonHovered: {
    backgroundColor: '#8B47E6',
    transform: [{ scale: 1.02 }],
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  secondaryButtonHovered: {
    borderColor: 'rgba(255, 255, 255, 0.4)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  buttonIcon: {
    fontSize: 18,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButtonText: {
    color: '#fff',
  },
  resultsContainer: {
    gap: 24,
  },
  statsCard: {
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
  },
  statsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 24,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 20,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statBadge: {
    width: 80,
    height: 80,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
  },
  statLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  resultsCard: {
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  jobsList: {
    gap: 16,
  },
  jobCard: {
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
  },
  jobHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  rankBadge: {
    backgroundColor: '#A259FF',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  rankText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
  },
  jobMetrics: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  metric: {
    flex: 1,
    minWidth: 150,
  },
  metricLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 6,
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  decisionBadge: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  decisionText: {
    color: '#4CAF50',
    fontSize: 14,
    fontWeight: '600',
  },
  oddsBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  oddsText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  timelineBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  timelineIcon: {
    fontSize: 14,
  },
  challengeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  challengeIcon: {
    fontSize: 14,
  },
  framingSection: {
    backgroundColor: 'rgba(162, 89, 255, 0.1)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  framingLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    opacity: 0.8,
  },
  framingText: {
    fontSize: 14,
    lineHeight: 20,
  },
  riskSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    borderRadius: 8,
    padding: 10,
  },
  riskIcon: {
    fontSize: 16,
  },
  riskText: {
    fontSize: 13,
    flex: 1,
  },
  skipList: {
    gap: 12,
  },
  skipCard: {
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderLeftWidth: 3,
  },
  skipTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 6,
  },
  skipReason: {
    fontSize: 13,
    lineHeight: 18,
  },
});
